import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import Footer from 'common/footer';
import GNB from 'common/header';
import ModalContainer from 'common/modal';
import { Position } from 'common/searchBox';
import UnsupportedBrowser from 'common/unsupportedBrowser';
import { useInfoParamsContext } from 'contexts/infoParams';
import {
  IModal,
  Modal,
  ModalFuncContext,
  ModalPosition,
} from 'contexts/modalFunc';
import {
  defaultRecommendSearchParams,
  getRecommendParams,
  useRecommendParamsContext,
} from 'contexts/recommendParams';
import { AP_CODES, SeatClass, TripType } from 'contexts/searchParams';
import { RecommendModal } from 'everywhere/common';
import CityMap from 'everywhere/common/map';
import ThemeFilter from 'everywhere/common/themeFilter';
import styles from 'everywhere/index.module.scss';
import SearchCondition from 'list/common/searchCondition';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import FilterModal from './filterModal';
import InlineFilter from './inlineFilter';
import List from './list';
import GET_RECOMMEND_FLIGHT from './recommend.gql';
import Recommend from './recommendModal';
import ThemeModal from './themeModal';
import { FilterModalSection, IData, IRecommend } from './types';
import { formatRecommends } from './utils';

const cx = classNames.bind(styles);

const markerClickHandler =
  (
    onSelect: (type: Modal, item?: IRecommend | undefined) => void,
    recommendMap: Map<string, IRecommend>,
  ) =>
  (e: any, marker: any) =>
    onSelect(Modal.RECOMMEND, recommendMap.get(marker.destination.cityCode));

export default function Everywhere() {
  const { setInfoSearchParams } = useInfoParamsContext();
  const { recommendSearchParams, setRecommendSearchParams } =
    useRecommendParamsContext();

  const {
    query,
    query: { departure, months, viewType },
  } = useRouter();
  const [isListHidden, hideList] = useState(false);
  const [isMapView, toggleToMapView] = useState(viewType === 'map');
  const [modal, setModal] = useState<IModal>({
    type: Modal.NULL,
    position: ModalPosition.RECOMMEND,
  });
  const [recommendModal, setRecommendModal] = useState<RecommendModal>(
    RecommendModal.NULL,
  );
  const [selected, setSelected] = useState<IRecommend>();
  const { data, error, loading } = useQuery<IData>(GET_RECOMMEND_FLIGHT, {
    variables: {
      ...getRecommendParams(recommendSearchParams),
      recommendOnly: true,
      scity: departure,
    },
    ssr: false,
  });
  const { bounds, continents, countries, theme } = recommendSearchParams;
  const continentIds = query.continents?.toString().split(',') || [];

  function closeModal() {
    setRecommendModal(RecommendModal.NULL);
  }

  function onSelect(type: Modal, item?: IRecommend) {
    if (!item) return;
    const { departureCity, arrivalCity, isDomestic, sdate1, roundTripTime } =
      item;
    setInfoSearchParams(prev => ({
      ...prev,
      departureCity: {
        code: departureCity.iataCode,
        name: departureCity.cityName,
      },
      arrivalCity: {
        code: arrivalCity.iataCode,
        name: arrivalCity.cityName,
      },
      isDomestic,
      period: roundTripTime,
      departureDate: sdate1,
      isProductChange: true, // 상품만 바뀐다 -> true. period/direct -> false
    }));
    setSelected(item);
    setModal({ type, position: ModalPosition.RECOMMEND });
  }

  const recommendMap = new Map(
    data?.recommend.list.map(recommend => [
      recommend.arrivalCity.iataCode,
      recommend,
    ]),
  );

  const itinerary = useMemo(
    () => [
      {
        departureAirport: departure as string,
        arrivalAirport: AP_CODES.EVERYWHERE,
        departureDate: '',
      },
      {
        departureAirport: AP_CODES.EVERYWHERE,
        arrivalAirport: departure as string,
        departureDate: '',
      },
    ],
    [departure],
  );

  const filtered = useMemo(
    () =>
      data?.recommend.list.filter(
        ({
          arrivalCity: {
            location: { latitude: lat, longitude: lng },
          },
        }) => !bounds || bounds.hasLatLng({ lat, lng }),
      ) || [],
    [data, bounds],
  );

  const initialBounds = useMemo(() => {
    if (!data || !data.recommend.list.length) return [];
    const latitudes = data.recommend.list.map(
      ({
        arrivalCity: {
          location: { latitude },
        },
      }) => latitude,
    );
    const longitude = data.recommend.list.map(
      ({
        arrivalCity: {
          location: { longitude },
        },
      }) => longitude,
    );

    return [
      Math.min(...longitude),
      Math.min(...latitudes),
      Math.max(...longitude),
      Math.max(...latitudes),
    ];
  }, [data]);

  const mapList = useMemo(() => formatRecommends(filtered), [filtered]);

  const openFilter = useCallback((scrollElementId: FilterModalSection) => {
    setRecommendModal(RecommendModal.Filter);
    setTimeout(() => {
      const scrollTargetElement = document.getElementById(scrollElementId);
      if (scrollTargetElement) {
        scrollTargetElement.scrollIntoView({ block: 'start' });
      }
    }, 0);
  }, []);

  const boundsChangedHandler = (bounds: naver.maps.LatLngBounds) => {
    setRecommendSearchParams(prev => ({ ...prev, bounds }));
  };

  function resetFilters() {
    setRecommendSearchParams(({ bounds }) => ({
      ...defaultRecommendSearchParams,
      bounds,
    }));
  }

  return (
    <ModalFuncContext.Provider value={setModal}>
      <div
        className={cx('flights', 'Everywhere')}
        data-container="everywhere"
      >
        <div className={cx('Everywhere')}>
          <UnsupportedBrowser />

          <GNB fullbleed={true} />

          <div className={cx('search')}>
            <SearchCondition
              adult={1}
              child={0}
              infant={0}
              continentId={
                continentIds.length === 1 ? +continentIds[0] : undefined
              }
              countryCode={countries || undefined}
              departureMonths={new Set((months as string)?.split(','))}
              disablePriceAlert
              expandMultiCity
              fareType={SeatClass.Y}
              itinerary={itinerary}
              position={Position.EVERYWHERE}
              tripType={TripType.EVERYWHERE}
            />
            <ThemeFilter
              setRecommendModal={setRecommendModal}
              themeFilter={id => {
                setRecommendSearchParams(prev => ({
                  ...prev,
                  bounds: undefined,
                  countries: '',
                  theme: `${id}`,
                }));
              }}
              selectedTheme={+theme}
            />
            <div className={cx('filter_wrapper_mobile')}>
              <InlineFilter openFilter={openFilter} />
            </div>
          </div>
          <main>
            <article className={cx('list')} aria-expanded={!isListHidden}>
              <div className={cx('filter_wrapper_pc')}>
                <InlineFilter openFilter={openFilter} />
              </div>
              <button
                className={cx('toggle')}
                type="button"
                aria-expanded={isMapView}
                data-event-nlog="click"
                data-event-area-code={`rcm.${isMapView ? 'listview' : 'mapview'}`}
                onClick={() => {
                  toggleToMapView(prev => !prev);
                }}
              >
                {isMapView ? '목록보기' : '지도보기'}
              </button>
              {recommendModal === RecommendModal.Filter && (
                <FilterModal onClose={closeModal} />
              )}
              <List
                data={filtered}
                error={!!error}
                loading={loading}
                onReset={resetFilters}
                onSelect={onSelect}
              />
            </article>
            <aside className={cx('map')} aria-expanded={isMapView}>
              <CityMap
                initialBounds={initialBounds}
                isFilterResetRequired={
                  !(!continents.size && !countries && !theme)
                }
                isListHidden={isListHidden}
                isLoading={loading}
                isMapView={isMapView}
                list={mapList}
                boundsChangedHandler={boundsChangedHandler}
                markerClickCode="rcm.mapselect"
                markerClickHandler={markerClickHandler(onSelect, recommendMap)}
                onReset={resetFilters}
              />
              <button
                type="button"
                className={cx('expand')}
                aria-label="지도 확장 토글"
                aria-expanded={isListHidden}
                data-event-nlog="click"
                data-event-area-code={`rcm.${isListHidden ? 'mapopenthetop' : 'mapclosethetop'}`}
                onClick={() => {
                  hideList(prev => !prev);
                }}
              />
            </aside>
          </main>

          <Footer />

          {modal.position !== ModalPosition.RECOMMEND ? (
            <ModalContainer modal={modal} setModal={setModal} />
          ) : (
            modal.type !== Modal.NULL &&
            selected && (
              <Recommend
                modal={modal}
                selected={selected}
                setModal={setModal}
              />
            )
          )}

          {recommendModal === RecommendModal.Theme && (
            <ThemeModal onClose={closeModal} />
          )}
        </div>
      </div>
    </ModalFuncContext.Provider>
  );
}
